/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * Safe wrapper for Clerk's useAuth hook that works in test mode
 *
 * This hook always calls Clerk's useAuth unconditionally to comply with
 * React's rules of hooks, but returns mock values when test mode is active.
 */

import { DEFAULT_TEST_USERS } from "@/lib/testing/test-mode-simple";
import { useAuth as useClerkAuth } from "@clerk/nextjs";
import type { GetToken } from "@clerk/types";

// Test mode detection
// NOTE: We do NOT check NODE_ENV === "test" because that's true for both
// unit tests (Vitest) and E2E tests (Playwright). Unit tests should use
// mocked values, not test bypass. Only E2E tests should activate test mode.
const isTestModeActive = (): boolean => {
  const envTestMode =
    process.env["NEXT_PUBLIC_E2E_TEST_MODE"] === "true" ||
    process.env["E2E_TEST_MODE"] === "true";

  if (typeof window !== "undefined") {
    return envTestMode || Boolean(window.__TEST_BYPASS_ENABLED);
  }

  return envTestMode;
};

/**
 * Safe wrapper for useAuth that works in test mode
 */
export function useAuthSafe(): ReturnType<typeof useClerkAuth> {
  // Always call hooks unconditionally to comply with rules of hooks
  const clerkAuth = useClerkAuth();
  const testModeActive = isTestModeActive();

  if (testModeActive) {
    // Return mock auth values for test mode
    const testUser = DEFAULT_TEST_USERS.owner;

    const mockGetToken: GetToken = async () => {
      // Return a mock token for test mode
      return "test-token";
    };

    const mockSignOut = async () => {
      // No-op in test mode
      return Promise.resolve();
    };

    // Type assertion is safe here - we're providing a test implementation that satisfies the interface
    return {
      isLoaded: true,
      isSignedIn: true,
      userId: testUser.id,
      sessionId: "test-session-id",
      orgId: testUser.organizationId,
      orgRole: "org:admin" as const,
      orgSlug: null,
      getToken: mockGetToken,
      signOut: mockSignOut,
    } as ReturnType<typeof useClerkAuth>;
  }

  // In normal mode, return Clerk's useAuth result
  return clerkAuth;
}
